import { useCallback, useState } from 'react';
import { FaSpotify } from 'react-icons/fa';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import ReactInputVerificationCode from 'react-input-verification-code';
import { Col, Row } from 'react-bootstrap';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { FiLock, FiMail } from 'react-icons/fi';
import { Formik, Form } from 'formik';
import { HiCheck } from 'react-icons/hi';
import { Button } from '../../../components/Button';
import { useQuery } from '../../../utils/useQuery';
import { Input } from '../../../components/Input';
import api from '../../../services/api';
import { ValidationPassword } from '../../../layouts/auth/styles';

interface ContainerProps {
  error: boolean;
}

const Container = styled.div<ContainerProps>`
  --ReactInputVerificationCode-itemWidth: 3.1rem;
  --ReactInputVerificationCode-itemHeight: 3.5rem;
  .ReactInputVerificationCode__item {
    box-shadow: ${({ error }) =>
      error ? 'inset 0 0 0 1px #dc3545' : 'inset 0 0 0 1px #ccc'};
  }

  span {
    font-size: 0.8rem;
  }
`;

interface SubmitProps {
  password: string;
  email: string;
}

export const ConfirmMail = () => {
  const query = useQuery();
  const history = useHistory();
  const codeQuery = query.get('code');
  const emailQuery = query.get('email');
  const theme: DefaultTheme = useTheme();
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<
    Array<'UPPERCASE' | 'LOWERCASE' | 'MIN' | 'SPECIAL' | 'NUMBER'>
  >([]);
  const [validatedCode, setValidatedCode] = useState(codeQuery);
  const [error, setError] = useState(false);

  const handleSubmit = useCallback(
    async ({ email, password }: SubmitProps) => {
      try {
        setLoading(true);
        setError(false);
        if (validatedCode?.length !== 6) {
          setError(true);
          return;
        }
        await api.post('/confirmation_email', {
          email: emailQuery || email,
          password,
          token: validatedCode,
        });
        history.push('/');
        toast.success('Senha salva com sucesso, faça seu login');
      } catch (err: any) {
        if (err.response.data.message) {
          toast.error(err.response.data.message);
          return;
        }
        toast.error('Erro ao salvar senha');
      } finally {
        setLoading(false);
      }
    },
    [validatedCode, history],
  );

  const handleValidationPassword = (e: string) => {
    if (e.length >= 8) {
      setValidation(old => (old.indexOf('MIN') >= 0 ? old : [...old, 'MIN']));
    } else {
      setValidation(old => {
        if (old.indexOf('MIN') >= 0) {
          return old.filter(item => item !== 'MIN');
        }
        return old;
      });
    }

    if (/\d/.test(e)) {
      setValidation(old =>
        old.indexOf('NUMBER') >= 0 ? old : [...old, 'NUMBER'],
      );
    } else {
      setValidation(old => {
        if (old.indexOf('NUMBER') >= 0) {
          return old.filter(item => item !== 'NUMBER');
        }
        return old;
      });
    }

    if (/[A-Z]+/.test(e)) {
      setValidation(old =>
        old.indexOf('UPPERCASE') >= 0 ? old : [...old, 'UPPERCASE'],
      );
    } else {
      setValidation(old => {
        if (old.indexOf('UPPERCASE') >= 0) {
          return old.filter(item => item !== 'UPPERCASE');
        }
        return old;
      });
    }

    if (/[a-z]/.test(e)) {
      setValidation(old =>
        old.indexOf('LOWERCASE') >= 0 ? old : [...old, 'LOWERCASE'],
      );
    } else {
      setValidation(old => {
        if (old.indexOf('LOWERCASE') >= 0) {
          return old.filter(item => item !== 'LOWERCASE');
        }
        return old;
      });
    }

    if (/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(e)) {
      setValidation(old =>
        old.indexOf('SPECIAL') >= 0 ? old : [...old, 'SPECIAL'],
      );
    } else {
      setValidation(old => {
        if (old.indexOf('SPECIAL') >= 0) {
          return old.filter(item => item !== 'SPECIAL');
        }
        return old;
      });
    }
  };

  const schema = yup.object({
    password: yup
      .string()
      .required('Digite sua senha')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~])[A-Za-z\d`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]{8,}$/,
        'Coloque todas as restrições de senha',
      ),
    email: emailQuery
      ? yup.string()
      : yup
          .string()
          .email('Informe um email válido')
          .required('Digite seu email'),
  });

  return (
    <Formik
      initialValues={{ password: '', email: '' }}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      <Form>
        <h5 className="text-center">
          <FaSpotify
            size={65}
            color={theme.colors.secondary}
            className="mr-2"
          />
        </h5>
        <h4 className="text-center mt-3">Cadastro de senha</h4>
        <h6 className="mb-3">
          Lorem ipsum dolor sit amet consectetur adipiscing elit
        </h6>
        <Container error={error}>
          <Row>
            <Col md={12}>
              <ReactInputVerificationCode
                length={6}
                onCompleted={e => setValidatedCode(e)}
                onChange={e => setValidatedCode(e.split('·')[0])}
                value={validatedCode || undefined}
              />
              {error && (
                <span className="text-danger d-block mt-1">
                  Informe o código completo
                </span>
              )}
            </Col>
          </Row>
        </Container>

        {!emailQuery && (
          <Input type="email" name="email" placeholder="Email" icon={FiMail} />
        )}

        <Input
          type="password"
          name="password"
          placeholder="Senha"
          icon={FiLock}
          onChangeCapture={(e: React.ChangeEvent<any>) =>
            handleValidationPassword(e.target.value)
          }
        />

        <ul className="d-flex align-items-center justify-content-start flex-wrap mt-2">
          <ValidationPassword check={validation.indexOf('SPECIAL') >= 0}>
            <HiCheck />
            <span>1 caracter especial</span>
          </ValidationPassword>
          <ValidationPassword check={validation.indexOf('MIN') >= 0}>
            <HiCheck />
            <span>8 ou mais caracteres</span>
          </ValidationPassword>
          <ValidationPassword check={validation.indexOf('NUMBER') >= 0}>
            <HiCheck />
            <span>1 número</span>
          </ValidationPassword>
          <ValidationPassword check={validation.indexOf('UPPERCASE') >= 0}>
            <HiCheck />
            <span>1 letra maiúscula</span>
          </ValidationPassword>
          <ValidationPassword check={validation.indexOf('LOWERCASE') >= 0}>
            <HiCheck />
            <span>1 letra minúscula</span>
          </ValidationPassword>
        </ul>
        <Button
          className="mt-4"
          type="submit"
          background={theme.colors.secondary}
          loading={loading || undefined}
          color="#fff"
        >
          Salvar
        </Button>
      </Form>
    </Formik>
  );
};
